import ListItem from './ListItem';
import Filter from './Filter';
import FilterSelected from './FilterSelected';
import Pagination from './Pagination';
import Search from './Search';
import {getTranslation} from './translations';
import SortSelect from './SortSelect';

/* eslint-disable no-undef */
const ref = typeof Vue !== 'undefined' ? Vue.ref : null;
const watch = typeof Vue !== 'undefined' ? Vue.watch : null;
const reactive = typeof Vue !== 'undefined' ? Vue.reactive : null;
const computed = typeof Vue !== 'undefined' ? Vue.computed : null;
const onMounted = typeof Vue !== 'undefined' ? Vue.onMounted : null;

// dynamicList?filter=col_1764336577816%3Anow%7C%3Bcol_1764346531243%3AItem%2CC
// dynamicList?filter=col_1764336577816%3Anow%7C%3Bcol_1764346531243%3AItem%2CC
// https://ubf.localhost/dynamicList?filter=col_1764336577816%3Anow%7C%3Bcol_1764346531243%3AItem%2CC

const template = `
	<div class="ubf-dynamicList" v-if="!loading" id="dynamicList" :class="config.theme">

		<div class="ubf-dynamicList__filters" :class="{'ubf-dynamicList__filters--hidden' : filterset === 'hidden', 'ubf-dynamicList__filters--collapsed' : filterset === 'collapsed', 'ubf-dynamicList__filters--expanded' : filterset === 'expanded', 'js-active': isFiltersActive || filterset === 'expanded'}" v-if="definitions" role="region" :aria-label="trans('filter')">
			<button class="ubf-dynamicList__filtersetToggle" @click="onToggleFilterset()" :aria-expanded="isFiltersActive || filterset === 'expanded'" aria-controls="ubf-dynamicList__filterset">{{ isFiltersActive ? trans('closeFilter') : trans('openFilter') }}</button>
			<div class="ubf-dynamicList__filterset" id="ubf-dynamicList__filterset" v-if="isFiltersActive || filterset === 'expanded'">
				<ubfdl-search v-if="showSearch" :label="trans('search')" :value="search.value" :description="trans('search')" @input="onSearchInput" ></ubfdl-search>
				<ubfdl-sortSelect v-if="showSort" @change="onSortSelectChange" :fields="sortableFields" :sortField="sortField.value"></ubfdl-sortSelect>
				<header v-if="hasFilterableFields" class="ubf-dynamicList__sortSelectLabel ubf-field__label">Filter</header>
				<template v-if="hasFilterableFields" v-for="definition in definitions">
					<ubfdl-filter v-if="definition.filterable" :definition="definition" @filter-change="onFilterChange" :value="filters[definition.id]"/>
				</template>
			</div>
		</div>
		<div class="ubf-dynamicList__list">

			<ubfdl-filterSelected :filters="filters" :search="search" :linkedId="linkedId" :definitions="definitions" @filter-remove="onFilterRemove" @search-remove="onSearchRemove" @linked-id-remove="onLinkedIdRemove"></ubfdl-filterSelected>

			<div class="ubf-dynamicList__result" v-if="filteredItems.length > 0">


				<div class="ubf-dynamicList__resultHeader">
					<ubfdl-pagination :total="filteredItems.length" :page-size="pageSize" v-model:page="currentPage" :maxButtons="5"/>
				 	<div class="ubf-dynamicList__resultCount" aria-live="polite" aria-atomic="true">{{ filteredItems.length }} {{trans('results')}}</div>
				</div>

				<ubfdl-listitem
					v-for="(item, index) in pagedItems"
					:key="item.id"
					:id="item.id"
					:title="item.title"
					:url="item.url"
					:target="item.target"
					:index="index"
					:data="item.data"
					:definitions="definitions"
					:filters="filters"
					:config="config"
				/>

				<ubfdl-pagination :total="filteredItems.length" :page-size="pageSize" v-model:page="currentPage"/>
			</div>

			<div class="ubf-dynamicList__no-result" role="status" aria-live="polite" v-else>
				{{trans('noresult')}}
			</div>
		</div>
	</div>
	<div class="ubf-dynamicList__loading" role="status" aria-live="polite" v-else>
		{{trans('Loading')}}...
	</div>
`;

export default {
	components: {
		'ubfdl-search': Search,
		'ubfdl-sortSelect': SortSelect,
		'ubfdl-filter': Filter,
		'ubfdl-filterSelected': FilterSelected,
		'ubfdl-listitem': ListItem,
		'ubfdl-pagination': Pagination,
	},
	template: template,
	props: {},
	setup: function (props) {

		const loading = ref(true);
		const loadedData = ref(null);
		let requestFilter = ref(null);

		const items = computed(() => {
			if (!loadedData.value || !loadedData.value.data || !loadedData.value.data.items) return [];
			return loadedData.value.data.items.map((item, index) => ({
				id: item.id ? item.id : '' + index,
				title: item.title,
				url: item.url,
				target: item.target,
				data: item.data
			}));
		});
		const definitions = computed(() => {
			if (!loadedData.value || !loadedData.value.data || !loadedData.value.data.definitions) return [];
			const defs = loadedData.value && loadedData.value.data ? loadedData.value.data.definitions : [];
			defs.forEach(d => {
				if (d.type === 'single-select' || d.type === 'multi-select') {
					// migration: if preset is an array of strings convert it to an array of objects containing the id and label
					if (Array.isArray(d.preset) && d.preset.every(p => typeof p === 'string')) {
						d.preset = d.preset.map(p => ({id: p, label: p}));
					}
				}
			});
			return defs;
		});
		const hasFilterableFields = computed(() => definitions.value.some(d => d.filterable));
		const sortableFields = computed(() => {
			const sortable = definitions.value.filter(d => d.sortable);
			return sortable.map(d => ({
				id: d.id,
				title: d.title
			}));
		});
		const config = computed(() => {
			if (!loadedData.value || !loadedData.value.config) return {
				id: '',
				name: 'Dynamic list',
				pagination: '5',
				order: 'ASC',
				'order-field': '',
				filterset: 'expanded',
				showLinks: true,
				theme: 'default',
				showSort: true,
				showSearch: true
			};
			return loadedData.value.config;
		});
		const search = reactive({value: ''});
		const linkedId = reactive({value: ''});
		const filters = reactive({});
		const sortField = reactive({value: ''});

		// filter=col_1764336531254%253Aone%252C%2520option%257Ctwo%252C%2520option%253Bcol_1764336555154%253A44
		// decoded value: col_1764336531254:one, option|two, option;col_1764336555154:44

		// get value of request parameter "filter"
		// const requestFilter = computed(() => {

		onMounted(() => {
			//	let url = window.location.href + '?__xsl=/_src/api/dynamic-component/dynamic-component-json-api.xsl';
			const u = new URL(window.location.href);
			// überschreibt vorhandenen __xsl-Parameter (oder fügt ihn hinzu)
			u.searchParams.set('__xsl', '/_src/api/dynamic-component/dynamic-component-json-api.xsl');
			let url = u.href;

			if (url.includes('ubf.cdlx.dev') || url.includes('ubf.localhost')) {
				url = '/listdata.json';
				console.warn('dev system: override src url with:', url);
			}

			fetch(url)
				.then((response) => response.json())
				.then((data) => {
					loadedData.value = data;
					loading.value = false;

					// get preset filters from request parameter "filter"
					// store in requestFilter
					let rf = new URLSearchParams(window.location.search).get('filter');
					const defs = loadedData.value.data.definitions;
					if (rf && rf.length > 0) {
						rf = decodeURIComponent(rf);
						const requestFilterValues = rf.split(';').map((f) => {
							const [attr, val] = f.split(':');
							const definition = defs.find(def => def.id === attr);
							if (definition.type === 'multi-select') {
								const values = val.split('|');
								// filter out values that are not in the definition.preset array
								const filteredValues = values.filter(v => definition.preset.map(o => o.id).includes(v));
								return {[attr]: filteredValues};
							} else if (definition.type === 'single-select') {
								if (definition.preset.includes(val)) {
									return {[attr]: val};
								} else {
									return null;
								}
							} else if (definition.type === 'date') {
								const dates = val.split('|');
								return {[attr]: {from: dates[0], to: dates.length > 0 ? dates[1] : ''}};
							} else {
								return {[attr]: val};
							}
							//return {[attr]: definition.type === 'multi-select' ? val.split('|') : val};
						});
						requestFilter = requestFilterValues.reduce((acc, curr) => {
							for (const [key, value] of Object.entries(curr)) {
								acc[key] = value;
							}
							return acc;
						}, {});
						Object.assign(filters, requestFilter);
					}
				})
				.catch((e) => {
					loading.value = false;
				});

		});

		// get the hash parameter from the url and trigger a search with that
		const hash = window.location.hash.substr(1);
		if (hash) {
			linkedId.value = hash;
		}
		// watch for window.location.hash changes and set linkedId
		window.onhashchange = function () {
			linkedId.value = window.location.hash.substr(1);
			window.scrollTo(0, 0);
		};


		const normalize = (val) => (typeof val === 'string' ? val.trim().toLowerCase() : (val === null || val === undefined ? '' : String(val).trim().toLowerCase()));

		const linkedItems = computed(() => {
			const allItems = items.value || [];

			return allItems.filter(item => {
				const id = item.id || {};
				if (linkedId && linkedId.value === '') {
					return true;
				}
				return id === linkedId.value;
			});
		});

		const searchedItems = computed(() => {
			const allItems = linkedItems.value || [];

			return allItems.filter(item => {
				const data = item.data || {};
				if (search.value === '') {
					return true;
				}
				// search in all properties of data
				for (const key in data) {
					if (!data.hasOwnProperty(key)) continue;
					const defs = definitions.value.find(d => d.id === key);
					if (defs.type === 'multi-select') {
						// resolve labels from definition for search
						const searchString = data[key].map(v =>
							defs.preset.find(p => p.id === v))
							.map(v => (v ? v.label : ''))
							.join(' ');
						if (normalize(searchString).includes(normalize(search.value))) {
							return true;
						}
					}
					if (Object.hasOwn(data, key)) {
						if (normalize(JSON.stringify(data[key])).includes(normalize(search.value))) {
							return true;
						}
						if (normalize(JSON.stringify(item.title)).includes(normalize(search.value))) {
							return true;
						}
					}
				}
				return false;
			});
		});

		const filteredItems = computed(() => {
			const allItems = searchedItems.value || [];
			const defsById = {};
			(definitions.value || []).forEach(d => {
				defsById[d.id] = d;
			});
			const filtered = allItems
				.filter(item => {
					const data = item.data || {};
					let match = true;

					// evaluate all active filters
					for (const key in filters) {
						if (Object.hasOwn(filters, key)) {

							const def = defsById[key];
							if (!def) continue;
							const fval = filters[key];
							// skip inactive/empty filters
							if (def.type === 'multi-select') {
								if (!Array.isArray(fval) || fval.length === 0) continue;
							} else if (normalize(fval) === '') {
								continue;
							}
							const raw = data[key];
							const rawStr = raw === null ? '' : String(raw);

							if (def.type === 'text' || def.type === 'textarea') {
								// full text search
								if (!normalize(rawStr).includes(normalize(fval))) {
									match = false;
								}
							} else if (def.type === 'link') {
								if (!normalize(raw.text).trim().includes(normalize(fval))) {
									match = false;
								}
							} else if (def.type === 'number') {
								// compare as strings after trim to match dataset strings
								if (String(rawStr).trim() !== String(fval).trim()) {
									match = false;
								}
							} else if (def.type === 'date') {
								//const itemDate = raw.toISOString();
								const fromVal = fval.from === 'now' ? (new Date()).toISOString().split('T')[0] : fval.from;
								const toVal = fval.to === 'now' ? (new Date()).toISOString().split('T')[0] : fval.to;
								if (!
									(
										(fromVal === null || fromVal === '' || rawStr >= fromVal) &&
										(toVal === null || toVal === '' || rawStr < toVal)
									)
								) {
									match = false;
								}
							} else if (def.type === 'interval') {
								if (!raw) continue;
								const start = raw.start === null ? '' : String(raw.start);
								const end = raw.end === null ? '' : String(raw.end);
								const fromVal = fval.from === 'now' ? (new Date()).toISOString().split('T')[0] : fval.from;
								const toVal = fval.to === 'now' ? (new Date()).toISOString().split('T')[0] : fval.to;
								// return true if the event date (start, end) intersect the filter interval (fromVal, toVal)
								// ignore fromVal or toVal if not set (null or '')
								const hasFrom = fromVal !== null && fromVal !== '';
								const hasTo = toVal !== null && toVal !== '';
								if (!
									(
										(!hasFrom && !hasTo) || // keine limits
										(!hasFrom && (toVal > start)) || // noch nicht beendet
										(!hasTo && (fromVal <= end)) || // schon begonnen
										(toVal > start && fromVal <= end)
									) // schnitt
								) {
									match = false;
								}
							} else if (def.type === 'single-select') {
								if (String(rawStr).trim().toLowerCase() !== String(fval).trim().toLowerCase()) {
									match = false;
								}
							} else if (def.type === 'multi-select') {
								if (!raw || !Array.isArray(raw) || raw.length === 0) {
									match = false;
									continue;
								}
								// compare based on filter operator
								const operator = (def.filterOperator || 'AND').toUpperCase();
								const itemVals = raw.map(s => s.trim()).filter(Boolean);
								const selected = (Array.isArray(fval) ? fval : String(fval)).map(s => String(s).trim()).filter(Boolean);
								if (selected.length === 0) continue;
								if (operator === 'OR') {
									// OR: one of the selected values is included in itemVals
									const ok = selected.some(s => itemVals.some(iv => iv.toLowerCase() === s.toLowerCase()));
									if (!ok) {
										match = false;
									}
								} else {
									// AND: itemVals must include all selected values (but also can have more)
									const ok = selected.length > 0 && selected.every(s => itemVals.some(iv => iv.toLowerCase() === s.toLowerCase()));
									if (!ok) {
										match = false;
									}
								}
							}
						}
					}
					return match;
				});

			// sort "filtered" by the sort property named in sortBy
			let sortDirection = config.value.order || 'ASC';
			let sortBy = config.value['order-field'] || '';

			if (sortField.value === '' || sortField.value === ' ASC') {
				// default
				sortBy = config.value['order-field'];
				sortDirection = config.value.order;
				sortField.value = config.value['order-field'] + ' ' + config.value.order;
			} else {
				// user set
				sortBy = sortField.value.split(' ')[0];
				sortDirection = sortField.value.split(' ')[1];
			}

			const alphaNumericTitle = (a, b) => {
				const aValue = a.title || '\uffff';
				const bValue = b.title || '\uffff';
				return (sortDirection === 'ASC') ?
					aValue.localeCompare(bValue, 'de', {numeric: true}) :
					bValue.localeCompare(aValue, 'de', {numeric: true});
			};
			const alphaNumericField = (a, b) => {
				let aValue = a.data[sortBy] || '\uffff';
				let bValue = b.data[sortBy] || '\uffff';
				// replace comma with dot
				aValue = aValue.replace(',', '.');
				bValue = bValue.replace(',', '.');
				return (sortDirection === 'ASC') ?
					aValue.localeCompare(bValue, 'en', {numeric: true}) :
					bValue.localeCompare(aValue, 'en', {numeric: true});
			};

			if (sortBy) {
				if (sortBy === 'title') {
					filtered.sort(alphaNumericTitle);
				} else {
					filtered.sort(alphaNumericField);
				}
			}

			return filtered;
		});

		// pagination state

		const currentPage = ref(1);
		const pageSize = computed(() => Math.max(1, Number((config.value.pagination > 0 ? config.value.pagination : 1000) || 1000)));
		const totalPages = computed(() => {
			const total = filteredItems.value.length;
			return total > 0 ? Math.max(1, Math.ceil(total / pageSize.value)) : 1;
		});
		const filterset = computed(() =>
			config.value.filterset || 'expanded');

		const showSort = computed(() =>
			config.value.showSort || true);

		const showSearch = computed(() =>
			config.value.showSearch || true);

		// Clamp or reset page when filters or page size change
		watch([filteredItems, pageSize], () => {
			if (currentPage.value > totalPages.value) {
				currentPage.value = totalPages.value;
			}
			if (currentPage.value < 1) currentPage.value = 1;
		});

		const pagedItems = computed(() => {
			const start = (currentPage.value - 1) * pageSize.value;
			return filteredItems.value.slice(start, start + pageSize.value);
		});

		// handler for search change
		const onSearchInput = (val) => {
			search.value = val;
			currentPage.value = 0;
		};
		const onSortSelectChange = (val) => {
			sortField.value = val;
			currentPage.value = 0;
		};

		// handler for child filter change
		const onFilterChange = (payload) => {
			if (!payload || !payload.id) return;
			filters[payload.id] = payload.value;
		};
		// handler for child filter change
		const onFilterRemove = (payload) => {
			if (!payload || !payload.key || !payload.filter) return;
			if (Array.isArray(filters[payload.key])) {
				filters[payload.key] = filters[payload.key].filter(f => payload.filter !== f);
			} else {
				filters[payload.key] = '';
			}
		};
		const onSearchRemove = () => {
			search.value = '';
		};
		const onLinkedIdRemove = () => {
			linkedId.value = '';
			// remove the hash when it matches the linkedId
			const url = new URL(window.location.href);
			url.hash = '';
			window.history.replaceState({}, document.title, url.href);
		};
		return {
			trans: getTranslation,
			loading: loading,
			isFiltersActive: ref(false),

			items: items,
			definitions: definitions,
			config: config,
			sortableFields: sortableFields,

			search: search,
			sortField: sortField,
			linkedId: linkedId,
			filters: filters,
			requestFilter: requestFilter,
			filteredItems: filteredItems,
			pagedItems: pagedItems,
			currentPage: currentPage,
			totalPages: totalPages,
			filterset: filterset,
			pageSize: pageSize,
			showSort: showSort,
			showSearch: showSearch,
			onFilterChange: onFilterChange,
			onFilterRemove: onFilterRemove,
			onSearchInput: onSearchInput,
			onSearchRemove: onSearchRemove,
			onLinkedIdRemove: onLinkedIdRemove,
			onSortSelectChange: onSortSelectChange,
			hasFilterableFields: hasFilterableFields
		};
	},
	methods: {
		onToggleFilterset: function () {
			this.isFiltersActive = !this.isFiltersActive;
		}
	}
};

